import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { Link } from 'react-router-dom'
import { Validator } from 'remix-validated-form'
import { Button } from 'app/components/ui/button'
import { FormInput } from 'app/components/ui/form-input'
import { SmartForm } from 'app/components/ui/smart-form'

export type ForgotShadcnComponentProps = {
  email: string
  success: boolean
  error: string | undefined
  isSubmitting: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator: Validator<any>
  setEmail: (email: string) => void
  onSubmit: (data?: { email: string }) => void
}

export function ForgotShadcnComponent({
  email,
  success,
  error,
  isSubmitting,
  validator,
  setEmail,
  onSubmit,
}: ForgotShadcnComponentProps) {
  if (error) {
    return (
      <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
        <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-8 text-center shadow-sm sm:p-12">
          <h3 className="mb-3 text-2xl font-semibold text-strong">Something went wrong</h3>
          <p className="mb-5 text-center italic text-subtle">{error}</p>
          <p className="text-center text-subtle">
            You may have better success by requesting{' '}
            <Link to="/forgot" className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
              a new password reset
            </Link>
            .
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
      <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-8 text-center shadow-sm sm:p-12">
        {success ? (
          <>
            <h3 className="mb-3 text-2xl font-semibold text-strong">Check your email</h3>
            <p className="mb-6 text-subtle">
              If you have an account with us, you will receive an email with password reset instructions.
            </p>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 font-semibold text-default hover:text-strong"
            >
              <ArrowLeftIcon className="h-4 w-4" /> Back to login
            </Link>
          </>
        ) : (
          <div className="w-full max-w-md">
            <h2 className="mb-2 text-center text-2xl font-semibold text-strong">Forgot password?</h2>
            <p className="mb-8 text-center text-subtle">No worries, we'll send you reset instructions.</p>
            <SmartForm
              validator={validator}
              method="post"
              hideButtons={true}
              className="flex! w-full flex-col! gap-4"
              onSubmit={onSubmit}
            >
              <FormInput
                cols={6}
                label="Email"
                type="email"
                name="email"
                defaultValue={email}
                placeholder="Enter your email"
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
              <div className="mt-2 flex w-full flex-col items-center gap-4">
                <Button variant="primary" size="lg" type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Reset Password'}
                </Button>
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 font-semibold text-default hover:text-strong"
                >
                  <ArrowLeftIcon className="h-4 w-4" /> Back to login
                </Link>
              </div>
            </SmartForm>
          </div>
        )}
      </div>
    </div>
  )
}
